import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import type { PlaidItem, PlaidItemSummary } from "@finance/shared";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ApiError, deletePlaidItem, getPlaidItemSummary } from "@/lib/api";

interface DeletePlaidItemDialogProps {
  open: boolean;
  item: PlaidItem | null;
  onOpenChange: (open: boolean) => void;
  onSuccess: (deletedItemId: string) => void;
}

// Confirmation for `DELETE /plaid/items/:item_id`. Summary counts come from
// the matching summary endpoint when the dialog opens — keeping the call
// here rather than in the parent panel means the row list isn't slowed
// down by an extra COUNT per item.
export function DeletePlaidItemDialog({
  open,
  item,
  onOpenChange,
  onSuccess,
}: DeletePlaidItemDialogProps) {
  const [summary, setSummary] = useState<PlaidItemSummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !item) {
      setSummary(null);
      setSummaryError(null);
      setSubmitting(false);
      setError(null);
      return;
    }
    const ctrl = new AbortController();
    getPlaidItemSummary(item.item_id, ctrl.signal)
      .then((s) => {
        if (ctrl.signal.aborted) return;
        setSummary(s);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        setSummaryError(
          err instanceof Error ? err.message : "Couldn't load item details.",
        );
      });
    return () => ctrl.abort();
  }, [open, item]);

  if (!item) return null;

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await deletePlaidItem(item.item_id);
      onSuccess(item.item_id);
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 403) {
          setError(
            err.serverMessage ??
              "Only an admin can delete this Plaid connection.",
          );
        } else if (err.status === 404) {
          setError("That connection no longer exists. Refresh and try again.");
        } else {
          setError(err.serverMessage ?? "Couldn't delete the connection.");
        }
      } else {
        setError(
          err instanceof Error
            ? err.message
            : "Couldn't reach the server. Check your connection and retry.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !submitting && onOpenChange(next)}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Plaid connection</DialogTitle>
          <DialogDescription>
            {summary ? (
              <>
                This will permanently remove the connection to{" "}
                <span className="font-medium text-foreground">
                  {summary.institution_name}
                </span>{" "}
                and all{" "}
                <span className="font-medium text-foreground">
                  {summary.accounts_count}
                </span>{" "}
                {summary.accounts_count === 1 ? "account" : "accounts"} and{" "}
                <span className="font-medium text-foreground">
                  {summary.transactions_count}
                </span>{" "}
                {summary.transactions_count === 1
                  ? "transaction"
                  : "transactions"}{" "}
                synced from it. This cannot be undone.
              </>
            ) : summaryError ? (
              <>
                We couldn&apos;t load the cascade preview. You can still delete
                the connection — synced accounts and transactions for{" "}
                <span className="font-medium text-foreground">
                  {item.institution_name}
                </span>{" "}
                will be removed.
              </>
            ) : (
              <>Loading cascade preview…</>
            )}
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          >
            {error}
          </p>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Deleting…
              </>
            ) : (
              "Delete connection"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
